"use client";

import * as React from "react";
import Link from "next/link";
import { Bell, FolderLock, TrendingUp, CalendarClock, ShieldAlert, Settings, Loader2 } from "lucide-react";

type FeedItem = { id: string; type: string; title: string; body: string; href: string; at: string | null };

const ICONS: Record<string, React.FC<React.SVGProps<SVGSVGElement>>> = {
  access: FolderLock,
  opportunity: TrendingUp,
  renewal: CalendarClock,
  deal: ShieldAlert,
};

export function NotificationBell() {
  const [open, setOpen] = React.useState(false);
  const [items, setItems] = React.useState<FeedItem[] | null>(null);
  const [unread, setUnread] = React.useState(0);
  const ref = React.useRef<HTMLDivElement>(null);

  const load = React.useCallback(async () => {
    try {
      const res = await fetch("/api/member/notifications/feed", { cache: "no-store" });
      const json = await res.json().catch(() => ({}));
      setItems(Array.isArray(json.items) ? json.items : []);
      setUnread(typeof json.unread === "number" ? json.unread : 0);
    } catch {
      setItems([]);
    }
  }, []);

  React.useEffect(() => { load(); }, [load]);

  React.useEffect(() => {
    function onDoc(e: MouseEvent) { if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false); }
    document.addEventListener("mousedown", onDoc);
    return () => document.removeEventListener("mousedown", onDoc);
  }, []);

  return (
    <div className="relative" ref={ref}>
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        aria-label="Notifications"
        aria-expanded={open}
        className="relative p-2 rounded-xl hover:bg-lavender-50 text-gray-500"
      >
        <Bell className="w-5 h-5" />
        {unread > 0 && (
          <span className="absolute top-1 right-1 min-w-[16px] h-4 px-1 rounded-full bg-red-500 text-white text-[9px] font-bold flex items-center justify-center">
            {unread > 9 ? "9+" : unread}
          </span>
        )}
      </button>

      {open && (
        <div className="absolute right-0 mt-2 w-80 max-w-[92vw] bg-white rounded-xl border border-gray-100 shadow-card-hover z-50 overflow-hidden">
          <div className="flex items-center justify-between px-4 py-3 border-b border-gray-100">
            <p className="text-[13px] font-extrabold text-navy-500">Notifications</p>
            {items && items.length > 0 && <span className="text-[11px] text-gray-400">{items.length}</span>}
          </div>

          <div className="max-h-[60vh] overflow-y-auto">
            {items === null ? (
              <div className="flex items-center justify-center gap-2 py-8 text-gray-400 text-[12.5px]"><Loader2 className="w-4 h-4 animate-spin" /> Loading…</div>
            ) : items.length === 0 ? (
              <div className="text-center py-8 px-4">
                <span className="inline-flex w-10 h-10 rounded-full bg-lavender-100 text-cj-800 items-center justify-center mb-2"><Bell className="w-5 h-5" /></span>
                <p className="text-[13px] font-bold text-navy-500">You&apos;re all caught up</p>
                <p className="text-[11.5px] text-gray-400 mt-0.5">No new notifications right now.</p>
              </div>
            ) : (
              <ul className="divide-y divide-gray-50">
                {items.map((it) => {
                  const Icon = ICONS[it.type] ?? Bell;
                  return (
                    <li key={it.id}>
                      <Link href={it.href} onClick={() => setOpen(false)} className="flex gap-3 px-4 py-3 hover:bg-lavender-50/50 transition-colors">
                        <span className="w-8 h-8 rounded-lg bg-lavender-100 text-cj-800 flex items-center justify-center shrink-0"><Icon className="w-4 h-4" /></span>
                        <span className="min-w-0">
                          <span className="block text-[12.5px] font-bold text-navy-500">{it.title}</span>
                          <span className="block text-[11.5px] text-gray-500 leading-snug">{it.body}</span>
                        </span>
                      </Link>
                    </li>
                  );
                })}
              </ul>
            )}
          </div>

          <Link href="/my-account/notifications/" onClick={() => setOpen(false)} className="flex items-center justify-center gap-1.5 px-4 py-2.5 border-t border-gray-100 text-[12px] font-bold text-cj-800 hover:bg-lavender-50 transition-colors">
            <Settings className="w-3.5 h-3.5" /> Notification settings
          </Link>
        </div>
      )}
    </div>
  );
}
